import React, { FC } from 'react';
import { SvgIcon, Icons } from '../SvgIcon';
import { Button, ButtonProps, Theme, lighten, styled, useTheme } from '@material-ui/core';
import { Tooltip } from '../Tooltip';
import { SpriteIcon } from 'src/config/api/models/FontAwesome';
import { FontAwesomeSpriteIcon } from '../FontAwesomeLocal';

type Props = ButtonProps & {
  icon?: SpriteIcon;
  iconColor?: string;
  label?: string;
  tooltip?: React.ReactNode | undefined;
  visible?: boolean;
  colorVariant?: string;
  disabled?: boolean;
  onClick?: (e?: MouseEvent) => void;
  checked?: boolean;
};

const getColor = (color: string, type: 'hover' | 'icon', disabled: boolean, theme: Theme) => {
  switch (color) {
    case theme.palette.primary.main:
      return type === 'hover' ? theme.palette.primary.dark : theme.palette.common.white;
    case theme.config.progressColors.bronze.main:
      return type === 'hover'
        ? theme.config.progressColors.bronze.hover
        : theme.config.progressColors.bronze.icon;
    case theme.config.progressColors.silver.main:
      return type === 'hover'
        ? theme.config.progressColors.silver.hover
        : theme.config.progressColors.silver.icon;
    case theme.config.progressColors.gold.main:
      return type === 'hover'
        ? theme.config.progressColors.gold.hover
        : theme.config.progressColors.gold.icon;
    case theme.palette.common.white:
      return type === 'hover' ? theme.palette.grey['100'] : theme.palette.grey['500'];
    default:
      return type === 'hover'
        ? theme.palette.grey['100']
        : disabled
        ? theme.palette.grey['500']
        : theme.palette.primary.main;
  }
};

const StyledButton = styled(Button)<Theme, { colorvariant: string }>(({ theme, colorvariant }) => ({
  position: 'relative',
  display: 'inline-flex',
  width: '3rem',
  height: '3rem',
  flexShrink: 0,
  marginLeft: theme.spacing(1),
  opacity: 1,
  minWidth: 0,
  minHeight: 0,
  backgroundColor: colorvariant,
  '& span.MuiButton-label': {
    justifyContent: 'center',
  },
  '&:disabled': {
    opacity: 0.25,
  },
  '&:hover': {
    backgroundColor: colorvariant !== 'transparent' ? lighten(colorvariant, 0.3) : 'transparent',
  },
}));

const StyledLabel = styled('label')<Theme, { colorvariant: string }>(({ theme, colorvariant }) => ({
  fontSize: theme.typography.bodyXs.fontSize,
  fontFamily: theme.config.secondaryFontFamily,
  color: colorvariant,
}));

const StyledCheck = styled(SvgIcon)({
  position: 'absolute',
  top: 0,
  right: 0,
  transform: 'translate(50%, -50%)',
});

const ListItemActionButton: FC<Props> = ({
  icon,
  iconColor,
  label,
  tooltip = undefined,
  visible = true,
  colorVariant,
  disabled = false,
  onClick,
  checked = false,
  ...restProps
}) => {
  const theme = useTheme();

  if (!visible) return <></>;

  const btn = (
    <StyledButton
      colorvariant={colorVariant ? colorVariant : theme.palette.common.white}
      disabled={disabled}
      onClick={onClick}
      {...restProps}
    >
      {icon ? (
        <FontAwesomeSpriteIcon
          iconSize="md"
          color={
            disabled
              ? theme.palette.grey['500']
              : iconColor
              ? iconColor
              : theme.palette.primary.main
          }
          icon={icon}
        />
      ) : (
        label && (
          <StyledLabel
            colorvariant={
              colorVariant
                ? iconColor
                  ? iconColor
                  : getColor(colorVariant, 'icon', disabled, theme)
                : theme.palette.grey['500']
            }
          >
            {label}
          </StyledLabel>
        )
      )}
      {checked && <StyledCheck icon={Icons.CHECK} small />}
    </StyledButton>
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement="left">
      {btn}
    </Tooltip>
  ) : (
    btn
  );
};

export default ListItemActionButton;
